import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { contractsAPI, signaturesAPI } from '../services/api'
import type { Contract } from '../types'

export default function ContractViewer() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [contract, setContract] = useState<Contract | null>(null)
  const [loading, setLoading] = useState(true)
  const [signing, setSigning] = useState(false)
  const [actionMsg, setActionMsg] = useState('')

  useEffect(() => {
    if (id) {
      contractsAPI.getById(Number(id))
        .then((resp) => setContract(resp.data))
        .catch(() => {})
        .finally(() => setLoading(false))
    }
  }, [id])

  const handleAction = async (action: string) => {
    if (!contract) return
    setActionMsg(`${action}...`)
    try {
      switch (action) {
        case 'submit':
          await contractsAPI.submit(contract.id)
          break
        case 'approve':
          await contractsAPI.approve(contract.id)
          break
        case 'reject':
          await contractsAPI.reject(contract.id)
          break
        case 'publish':
          await contractsAPI.publish(contract.id)
          break
        case 'archive':
          await contractsAPI.archive(contract.id)
          break
      }
      // Refresh
      const resp = await contractsAPI.getById(contract.id)
      setContract(resp.data)
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error')
    } finally {
      setActionMsg('')
    }
  }

  const handleSign = async () => {
    if (!contract) return
    setSigning(true)
    try {
      await signaturesAPI.sign(contract.id, {
        party: 'provider',
        signer_name: 'Adder Computer',
        signer_dni: 'B61999785',
        signer_title: 'Representante Legal',
      })
      const resp = await contractsAPI.getById(contract.id)
      setContract(resp.data)
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al firmar')
    } finally {
      setSigning(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!contract) return
    try {
      const blob = await contractsAPI.getPdf(contract.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${contract.contract_number}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Error al descargar PDF')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  if (!contract) {
    return <div className="text-center py-5 text-muted">Contrato no encontrado</div>
  }

  const canEdit = contract.status === 'draft'
  const canSubmit = contract.status === 'draft'
  const canApprove = contract.status === 'review'
  const canPublish = contract.status === 'approved'
  const canSign = contract.status === 'published'
  const isSigned = contract.status === 'signed'

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="mb-1">
            <i className="bi bi-file-earmark-text me-2"></i>
            {contract.contract_number}
          </h1>
          <div className="text-muted small">{contract.title}</div>
        </div>
        <div className="d-flex gap-2">
          {canEdit && (
            <Link to={`/contracts/${contract.id}/edit`} className="btn btn-outline-secondary">
              <i className="bi bi-pencil me-1"></i> {t('common.edit')}
            </Link>
          )}
          <button className="btn btn-outline-primary" onClick={handleDownloadPdf}>
            <i className="bi bi-download me-1"></i> PDF
          </button>
        </div>
      </div>

      {/* Status & Actions */}
      <div className="card mb-3">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <span className={`badge bg-${statusBadge(contract.status)} me-2`} style={{ fontSize: '0.85rem', padding: '6px 14px' }}>
              {t(`contract.status.${contract.status}`)}
            </span>
            <span className="text-muted small">
              Creado: {contract.created_at ? new Date(contract.created_at).toLocaleDateString() : '—'}
              {contract.client_company_name && ` · Cliente: ${contract.client_company_name}`}
            </span>
          </div>
          <div className="d-flex gap-2">
            {canSubmit && (
              <button className="btn btn-warning btn-sm" onClick={() => handleAction('submit')} disabled={!!actionMsg}>
                Enviar a revisión
              </button>
            )}
            {canApprove && (
              <>
                <button className="btn btn-success btn-sm" onClick={() => handleAction('approve')} disabled={!!actionMsg}>
                  Aprobar
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleAction('reject')} disabled={!!actionMsg}>
                  Rechazar
                </button>
              </>
            )}
            {canPublish && (
              <button className="btn btn-primary btn-sm" onClick={() => handleAction('publish')} disabled={!!actionMsg}>
                Publicar
              </button>
            )}
            {canSign && !isSigned && (
              <button className="btn btn-success btn-sm" onClick={handleSign} disabled={signing}>
                {signing ? 'Firmando...' : 'Firmar contrato'}
              </button>
            )}
            {contract.status !== 'cancelled' && (
              <button className="btn btn-outline-danger btn-sm" onClick={() => handleAction('archive')} disabled={!!actionMsg}>
                Archivar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Contract content */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">Documento</div>
            <div className="card-body p-0">
              <iframe
                srcDoc={contract.rendered_html || ''}
                title="Contract"
                style={{ width: '100%', minHeight: '600px', border: 'none', background: 'white' }}
              />
            </div>
          </div>
        </div>

        {/* Side info */}
        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">Detalles</div>
            <div className="card-body">
              <div className="mb-2">
                <div className="text-muted small">Número</div>
                <div className="fw-semibold">{contract.contract_number}</div>
              </div>
              <div className="mb-2">
                <div className="text-muted small">Cliente</div>
                <div className="fw-semibold">{contract.client_company_name || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-muted small">Precio</div>
                <div className="fw-semibold">
                  {contract.price_amount ? `${contract.price_amount.toLocaleString()} €` : '—'}
                  {contract.price_frequency === 'monthly' ? '/mes' : contract.price_frequency === 'yearly' ? '/año' : ''}
                </div>
              </div>
              <div className="mb-2">
                <div className="text-muted small">Fecha inicio</div>
                <div className="fw-semibold">{contract.start_date || '—'}</div>
              </div>
              <div className="mb-2">
                <div className="text-muted small">Fecha fin</div>
                <div className="fw-semibold">{contract.end_date || '—'}</div>
              </div>
            </div>
          </div>

          {/* Signatures */}
          {contract.signatures && contract.signatures.length > 0 && (
            <div className="card">
              <div className="card-header">Firmas</div>
              <div className="card-body">
                {contract.signatures.map((sig) => (
                  <div key={sig.id} className="d-flex align-items-center gap-2 mb-2">
                    <div className={`badge bg-${sig.status === 'fully_signed' ? 'success' : 'secondary'} rounded-circle p-2`}>
                      <i className={`bi bi-${sig.status === 'fully_signed' ? 'check-lg' : 'clock'}`}></i>
                    </div>
                    <div>
                      <div className="fw-semibold small">{sig.signer_name}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {sig.party === 'provider' ? 'Proveedor' : 'Cliente'}
                        {sig.signed_at && ` · ${new Date(sig.signed_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function statusBadge(status: string): string {
  const map: Record<string, string> = {
    draft: 'secondary', review: 'warning', approved: 'info',
    published: 'primary', signed: 'success', expired: 'dark', cancelled: 'danger',
  }
  return map[status] || 'secondary'
}
